import { useCallback, useEffect, useMemo, useState } from "react";
import { Chart } from "../../components/Chart";
import { useFormatter } from "../../hooks/useFormatter";
import { paths } from "../../types/Defaults";

type ProductsChartProps = {
  products: any;
  fieldSelected: string;
  analytics: any;
  sorter: { dir: string; field: string };
  filters: any;
};

export function ProductsChart({
  products,
  fieldSelected,
  analytics,
  sorter,
  filters,
}: ProductsChartProps) {
  const [chartOptions, setChartOptions] = useState<any>();
  const getValueAtLargestDate = useCallback((analytic: any) => {
    const max = Math.max(...Object.keys(analytic).map((key) => parseInt(key)));

    return analytic[max];
  }, []);

  const filterProducts = useCallback(
    (products) => {
      let universe: any = products;

      for (const key in filters) {
        const filter = filters[key];

        if (filter.length > 0) {
          universe = universe.filter((row: any) => filter.includes(row[key]));
        }
      }

      return universe;
    },
    [filters],
  );

  const productsToSerieObj = useMemo(() => {
    const data = products.map((product: any) => ({
      name: product.name,
      id: product.id,
      file: product.name,
      type: product.type,
      geo: product.geo,
      method: product.method,
      rebalance: product.rebalance,
      theme: product.theme,
      uiLevel: product.uiLevel,
      perf1: getValueAtLargestDate(
        product.strategyAnalytics.priceAnalytics[
          "perf#YEARLY|price#YEARLY,1,SOLAR,H"
        ],
      ),

      perf3: getValueAtLargestDate(
        product.strategyAnalytics.priceAnalytics[
          "perf#YEARLY|price#YEARLY,3,SOLAR,H"
        ],
      ),

      perf10E: getValueAtLargestDate(
        product.strategyAnalytics.priceAnalytics[
          "perf#YEARLY|price#YEARLY,10,SOLAR,DIFF"
        ],
      ),

      perf10: getValueAtLargestDate(
        product.strategyAnalytics.priceAnalytics[
          "perf#YEARLY|price#YEARLY,10,SOLAR,H"
        ],
      ),

      sharpe: getValueAtLargestDate(
        product.strategyAnalytics.priceAnalytics["sharpe#H"],
      ),

      quaPositive: getValueAtLargestDate(
        product.strategyAnalytics.priceAnalytics[
          "winlose#H,false,1,true,QUARTERLY"
        ],
      ),

      quaPBetter: getValueAtLargestDate(
        product.strategyAnalytics.priceAnalytics[
          "winloseX#H,false,1,true,QUARTERLY"
        ],
      ),
    }));

    const filtered = filterProducts(data);

    filtered.sort((a: any, b: any) => {
      if (a[sorter.field] > b[sorter.field]) {
        return sorter.dir === "desc" ? -1 : 1;
      } else if (a[sorter.field] < b[sorter.field]) {
        return sorter.dir === "desc" ? 1 : -1;
      }

      return 0;
    });

    return filtered;
  }, [
    filterProducts,
    getValueAtLargestDate,
    products,
    sorter.dir,
    sorter.field,
  ]);

  const fmt = useFormatter();

  const formatter = useCallback(
    (value: any, field) => {
      switch (field) {
        case "perf1":
        case "perf3":
        case "perf10E":
        case "perf10":
        case "quaPositive":
        case "quaPBetter":
          return fmt.custom("number", {
            options: {
              decimals: 2,
              isPercentage: true,
              notAvailable: {
                input: "",
                output: "",
              },
            },
            output: "HTML",
            value: value,
            valueHelper: null,
          });
        case "sharpe": {
          return fmt.custom("number", {
            options: {
              decimals: 4,
              isPercentage: false,
              notAvailable: {
                input: "",
                output: "",
              },
            },
            output: "HTML",
            value: value,
            valueHelper: null,
          });
        }
      }
    },
    [fmt],
  );

  const draw = useCallback(
    (products, field) => {
      var data = [];
      for (var i = 0, N = products.length; i < N; i++) {
        var p = products[i];
        data.push([p.name, p[field]]);
      }
      var niceName = analytics[field];

      const format = (value: any) => formatter(value, field);

      const chartOptions = {
        chart: {
          type: "column",
        },
        animation: false,
        accessibility: { enabled: false },
        marginTop: 0,
        marginBottom: 0,
        spacingBottom: 0,
        spacingTop: 0,
        exporting: { enabled: false },
        credits: { enabled: false },
        title: {
          align: "center",
          text: niceName.toUpperCase(),
          style: {
            fontSize: "20px",
            fontFamily: "Arial, sans-serif",
          },
        },
        xAxis: {
          type: "category",
          labels: {
            enabled: data.length < 40,
            autoRotation: [-45],
            style: {
              fontSize: "13px",
              fontFamily: "Verdana, sans-serif",
              width: "200px", // Set a width to allow for word wrapping
            },
          },
        },
        yAxis: {
          visible: false,
          min: 0,
          title: { text: niceName },
          dataLabels: {
            formatter: function () {
              const self: any = this;
              return format(self.y);
            },
          },
        },
        legend: { enabled: false },
        tooltip: {
          borderColor: "#2a7090",
          style: {
            fontSize: "16px",
          },
          formatter: function () {
            const color = (this as any).y >= 0 ? "darkGreen" : "darkRed";
            return (
              "<strong>" +
              (this as any).point.name +
              ": </strong><b><span style='color:" +
              color +
              ";'>" +
              format((this as any).y) +
              "</b>"
            );
          },
        },
        series: [
          {
            name: niceName,
            cursor: "pointer",
            color: "darkGreen",
            negativeColor: "darkRed",
            groupPadding: 0,
            data: data,
            borderColor: "darkGrey",
            borderWidth: 1,
            borderRadius: 6,
            point: {
              events: {
                click: function () {
                  window.location.href = paths.productDetail.replace(
                    ":id",
                    products[(this as any).index].id,
                  );
                },
              },
            },
            dataLabels: {
              enabled: true,
              rotation: -90,
              color: "#FFFFFF",
              inside: true,
              verticalAlign: "top",
              formatter: function () {
                return format(this.y);
              },
              y: 50, // 10 pixels down from the top
              style: {
                fontSize: "16px", // Slightly smaller font size
                fontWeight: "normal", // Make it bold for emphasis
                textOutline: "none", // Remove text outline for a cleaner appearance
              },
            },
          },
        ],
      };

      setChartOptions(chartOptions);
    },
    [analytics, formatter],
  );

  useEffect(() => {
    if (productsToSerieObj && productsToSerieObj.length) {
      draw(productsToSerieObj, fieldSelected);
    }
  }, [draw, fieldSelected, productsToSerieObj]);

  return (
    <div>
      <Chart
        constructorType="chart"
        className="h-100"
        options={chartOptions}
      ></Chart>
    </div>
  );
}
